import { useState } from "react";
import { Check, Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FREE = ["Fino a 10 documenti in archivio", "Analisi documenti con AI", "Chat Chiedi a Burocrazy", "Scadenzario e promemoria email", "Guide e lettere PEC"];
const PRO = ["Documenti illimitati", "Fino a 300 richieste AI al giorno", "Tutto il piano Free", "Disdici quando vuoi"];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const isPro = user?.plan === "pro";
  const isGift = user?.pro_source === "gift";
  const giftLabel = user?.gift_pro_until === "forever" ? "per sempre" : `fino al ${fmtDate(user?.gift_pro_until)}`;

  const upgrade = async (provider) => {
    setBusy(provider);
    try {
      const origin_url = window.location.origin;
      const { data } = provider === "paypal" ? await api.post("/paypal/subscribe", { origin_url }) : await api.post("/payments/checkout", { origin_url });
      window.location.href = data.approve_url || data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };
  const cancel = async () => {
    if (!window.confirm("Vuoi disdire il piano Pro? Resterà attivo fino alla fine del periodo pagato.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Disdetta registrata");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="pricing-page">
      <PageHeader eyebrow="Piano Pro" title="Tutta la burocrazia, senza limiti" desc="Pagamento sicuro con carta (Stripe) o PayPal. Nessun vincolo." />
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        <div className="rounded-3xl border bg-white p-8" data-testid="plan-free-card">
          <h3 className="text-xl font-medium">Free</h3>
          <p className="mt-2 text-4xl font-display font-semibold">0 €</p>
          <ul className="mt-6 space-y-3 text-sm">{FREE.map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#1E3A8A]" />{t}</li>)}</ul>
          {!isPro && <p className="mt-8 text-sm text-slate-500" data-testid="plan-current-free">Il tuo piano attuale</p>}
        </div>
        <div className="rounded-3xl bg-[#1E3A8A] text-white p-8 relative overflow-hidden" data-testid="plan-pro-card">
          <Crown className="absolute -right-6 -top-6 h-32 w-32 text-white/10" />
          <h3 className="text-xl font-medium">Pro</h3>
          <p className="mt-2 text-4xl font-display font-semibold">9,99 € <span className="text-base font-normal text-blue-200">/ mese</span></p>
          <ul className="mt-6 space-y-3 text-sm">{PRO.map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#E07A5F]" />{t}</li>)}</ul>
          {isPro && !isGift ? (
            <div className="mt-8">
              <p className="font-medium" data-testid="plan-current-pro">Sei Pro. Grazie!</p>
              <p className="text-sm text-blue-200 mt-1" data-testid="plan-provider">Pagamento tramite {user.pro_source === "paypal" ? "PayPal" : "carta (Stripe)"}</p>
              {user.pro_cancel_at ? <p className="text-sm text-blue-200 mt-1">Disdetta registrata.</p> : <button onClick={cancel} className="mt-2 text-sm underline text-blue-200" data-testid="plan-cancel-btn">Disdici abbonamento</button>}
            </div>
          ) : (
            <div className="mt-8">
              {isGift && <p className="mb-3 font-medium" data-testid="plan-gift-label">Hai Pro in regalo {giftLabel}</p>}
              {user?.gift_pro_until !== "forever" && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => upgrade("stripe")} disabled={!!busy} className="rounded-full h-12 px-7 bg-[#E07A5F] hover:bg-[#d06a50] active:scale-95 transition-transform" data-testid="plan-upgrade-btn">
                    {busy === "stripe" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {isGift ? "Abbonati per continuare dopo il regalo" : "Passa a Pro con carta"}
                  </Button>
                  <Button onClick={() => upgrade("paypal")} disabled={!!busy} className="rounded-full h-12 px-7 bg-[#FFC439] text-[#003087] hover:bg-[#f2b92f] font-semibold active:scale-95 transition-transform" data-testid="plan-paypal-btn">
                    {busy === "paypal" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Abbonati con PayPal
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
